import {i18n} from "./i18n";
import {FieldHandler} from "./fieldHandler";
import UrlHelpers from "./url";
import * as mdc from "material-components-web";
import {checkbox, dialog, linearProgress, list, radio, ripple, select, snackbar, switchControl, textField, topAppBar} from "material-components-web";
import {MediamanagerFileInputHandler} from "./mediamanagerFileInput";
import {app_edit_changed_saved, app_edit_deleted} from "./dapiEventUris";
import {errorDto, ErrorHandler} from "./errorHandler";
import {isEndpointValid} from "./validation";
import {Escaping} from "./escaping";

export class AppEdit {
    private _dapi: Dapi = (<any>window).dapi;
    private _i18n: i18n = new i18n();
    private _snackbarElementId = 'appEdit-notification';
    private _delayTimer;
    private _newAddedApps: string[];

    //Material Objects
    private _mdcTextFields: { [id: string]: textField.MDCTextField } = {};
    private _mdcSelects: { [id: string]: mdc.select.MDCSelect } = {};
    private _mdcSelectHelperTexts: { [id: string]: mdc.select.MDCSelectHelperText } = {};
    private _mdcCheckboxes: { [id: string]: mdc.select.MDCCheckbox } = {};
    private _mdcLists: { [id: string]: mdc.list.MDCList } = {};
    private _mdcDialogs: { [id: string]: mdc.dialog.MDCDialog } = {};
    private _mdcRadioButtons: { [id: string]: mdc.select.MDCRadio } = {};

    constructor() {
        window.addEventListener('load', () => {
            document.body.style.height = "";
        })
        let scrollPageId = window.localStorage.getItem('scrollPageId');
        if (scrollPageId) {
            this._scrollToView(scrollPageId);
            window.localStorage.setItem('scrollPageId', '');
        }

        const scrollPos = window.localStorage.getItem('scrollPos');
        if (scrollPos) {
            this._scrollToPosition(parseInt(scrollPos));
            window.localStorage.setItem('scrollPos', '');
        }

        this._loadMaterial();
        this._setSaveButtonHandler();
        this._initializeMediamanagerInputs();
        this._setMediaImages();
        this._bindCollapsibles();
        this._setupAdministrateDependencyDialog();
        let btnDeleteApp = (<HTMLButtonElement>document.getElementById('delete-app-button'));
        if (btnDeleteApp) {
            btnDeleteApp.addEventListener('click', this._handleOpenDeleteModal);
        }
        let btnCopySecret = (<HTMLButtonElement>document.getElementById('btnCopySecret'));
        if (btnCopySecret) {
            btnCopySecret.addEventListener('click', this._copySecret);
        }
        let btnGenerateSecret = (<HTMLButtonElement>document.getElementById('btnGenerateSecret'));
        if (btnGenerateSecret) {
            btnGenerateSecret.addEventListener('click', this._handleOpenGenerateSecretModal);
        }

        const btnSubscribeApp = (document.getElementById('btn-subscribe-app'));
        if (btnSubscribeApp) {
            btnSubscribeApp.addEventListener('click', this._handleSubscribeBtnClick);
        }

        this.initApprovalState();

        const copyDocumentUrlButtons = document.querySelectorAll('.document-copyurl');
        if (copyDocumentUrlButtons) {
            Array.apply(null, copyDocumentUrlButtons).forEach((button: HTMLButtonElement) => {
                button.addEventListener('click', this._copyDocumentUrl);
            });
        }

        const deleteButtons = document.querySelectorAll('.document-delete');
        if (deleteButtons) {
            Array.apply(null, deleteButtons).forEach((button: HTMLButtonElement) => {
                button.addEventListener('click', this._openDeleteLegalDialog);
            });
        }

        const editDocumentButtons = document.querySelectorAll('.document-edit');
        if (editDocumentButtons) {
            Array.apply(null, editDocumentButtons).forEach((button: HTMLButtonElement) => {
                button.addEventListener('click', (e: Event) => {
                    const target = this._getParentFromEvent(e);
                    if (!target) {
                        return;
                    }
                    let index = 0;
                    const keyToIndex = {
                        "performanceSpecification": 0,
                        "privacyStatement": 1,
                        "subprocessorInformation": 2,
                        "generalTerms": 3,
                        "sla": 4,
                        "orderProcessingAgreements": 5
                    };
                    if (target.hasAttribute('data-key')) {
                        index = keyToIndex[target.dataset.key];
                    }
                    this._showDocumentDialog(index)
                });
            });
        }

        const addDocumentButton = document.getElementById('btn-add-document');
        if (addDocumentButton) {
            addDocumentButton.addEventListener('click', () => {
                this._showDocumentDialog();
            });
        }

        const addAppDependencyButton = <HTMLButtonElement>document.getElementById('btn-add-app-dependency');
        if (addAppDependencyButton) {
            addAppDependencyButton.addEventListener('click', () => {
                this._loadAppList((dto => {
                    this._renderAppListIntoAppDependencyDialog(dto);
                    this._writeAppListToMeta(dto);
                }), e => {
                    this._showErrorNotification('appEdit-notification', e);
                });
            });
        }
        this._loadAppList((dto => {
            console.debug('initial app list loaded');
            this._writeAppListToMeta(dto);
            this._renderDependenciesList();
        }), e => {
            this._showErrorNotification('appEdit-notification', `initial app list loading failed: ${e}`);
        });
        this._setDependenciesListObserver();

        const legalDocumentListItems = (<HTMLUListElement>document.getElementById('legal_document_list')).querySelectorAll('.mdc-list-item');
        Array.apply(null, legalDocumentListItems).forEach((li: HTMLLIElement) => {
            this._updateListElement(li);
        });

        const legalDocumentTitle = <HTMLInputElement>document.getElementById('appEditModalAddDocument-title');
        if (legalDocumentTitle) {
            legalDocumentTitle.addEventListener('input', () => {
                clearTimeout(this._delayTimer);
                this._delayTimer = setTimeout(() => {
                    this._validateDocumentDialog();
                }, 500);
            });
        }

        if (this._dapi.isMainResource()) {
            this._dapi.publishLocation(location.href);
        }

        const subscriptionNotificationMailCheckbox = this._mdcCheckboxes["useSubscriptionNotificationMail_checkbox"];
        if (subscriptionNotificationMailCheckbox) {
            subscriptionNotificationMailCheckbox.nativeControl_.addEventListener('change', () => {
                this._setSubscriptionNotification(subscriptionNotificationMailCheckbox.checked);
            });
        }

        const searchField = <HTMLInputElement>document.getElementById('appEditModalAddAppDependency_searchField-input');
        if (searchField) {
            searchField.addEventListener('keyup', this._handleSearchWithinNewAppDependencyList);
        }

        const dialogAppDep = this._mdcDialogs['appEditModalAddAppDependency'];
        if (dialogAppDep) {
            dialogAppDep.listen('MDCDialog:closed', this._handleDependencyDialogClosed);
        }
    }

    private _handleSubscribeBtnClick = () => {
        const appName = UrlHelpers.getMetaData('appName');
        const url = `${window.location.protocol}//${window.location.host}/center/subscriptionwizard?app=${appName}`;
        window.open(url, '_blank').focus();
    };

    private initApprovalState() {
        const approvalChip = (<HTMLElement>document.getElementById('approval-status'));
        const isCloudAdmin = UrlHelpers.getMetaData('sysAdmin') === "1"
        const userIsAppBuilder = approvalChip && approvalChip.dataset.appname && approvalChip.dataset.providerId && !isCloudAdmin;
        const userIsCloudAdmin = approvalChip && isCloudAdmin;

        if (userIsAppBuilder) {
            this._approvalStateAppBuilderAction(approvalChip);
        } else if (userIsCloudAdmin) {
            approvalChip.addEventListener('click', this._handleApproveChipClicked);
        }
    }

    private _handleApproveChipClicked = () => {
        const btn = <HTMLButtonElement>document.getElementById('appEditModalApproveAppBtnSave');
        btn.disabled = true;

        const approvalStateIsApproved = UrlHelpers.getMetaData('approvalState') === 'true';
        const allRequirementsExist = UrlHelpers.getMetaData('allRequirementsExist') === 'true'

        if (approvalStateIsApproved) {
            this._changeTextInApproveDialogToRejectApproval();
        }

        if (!allRequirementsExist && !approvalStateIsApproved) {
            this._showRequirementsMissingWarning();
        }

        const dialog = this._mdcDialogs['appEditModalApproveApp'];
        dialog.listen('MDCDialog:closed', this._handleAppApproveDialogClosed);
        dialog.layout();
        dialog.open();
    }

    private _showRequirementsMissingWarning() {
        const warningElement = <HTMLParagraphElement>document.getElementById('appEditModalApproveAppContentWarning');
        const warning = this._i18n.getTranslation('app_edit_modal_changeApprovalState_approve_warning');
        if (warningElement) {
            warningElement.innerHTML = `<i class="material-icons" style="vertical-align: bottom;">warning</i>&nbsp;${warning}`;
        } else {
            const contentElement = <HTMLDivElement>document.getElementById('appEditModalApproveAppContent');
            const p = document.createElement('p');
            p.id = 'appEditModalApproveAppContentWarning';
            p.innerHTML = `<i class="material-icons" style="vertical-align: bottom;">warning</i>&nbsp;${warning}`;
            contentElement.appendChild(p);
        }
    }

    private _changeTextInApproveDialogToRejectApproval() {
        const dialogTitle = <HTMLDivElement>document.getElementById('appEditModalApproveAppTitle');
        dialogTitle.innerText = this._i18n.getTranslation('app_edit_modal_changeApprovalState_unapprove_title');

        const contentText = <HTMLParagraphElement>document.getElementById('appEditModalApproveAppContentText');
        const appName = UrlHelpers.getMetaData('appName');
        const content = i18n.replaceTranslationPlaceholder(this._i18n.getTranslation('app_edit_modal_changeApprovalState_unapprove_content'), '{appName}', appName);
        contentText.innerHTML = `${content}`;

        const warningElement = <HTMLParagraphElement>document.getElementById('appEditModalApproveAppContentWarning');
        const warning = this._i18n.getTranslation('app_edit_modal_changeApprovalState_unapprove_warning');
        warningElement.innerHTML = `<i class="material-icons" style="vertical-align: bottom;">warning</i>&nbsp;${warning}`;

        const checkboxLabelElement = <HTMLLabelElement>document.getElementById('appEditModalApproveAppCheckboxLabel');
        const labelValue = this._i18n.getTranslation('app_edit_modal_changeApprovalState_unapprove_confirmCheckbox_label');
        checkboxLabelElement.innerText = labelValue;

        const btnSaveLabel = <HTMLSpanElement>document.getElementById('appEditModalApproveAppBtnSaveLabel');
        btnSaveLabel.innerText = this._i18n.getTranslation('app_edit_modal_changeApprovalState_unapprove_btnSave_label');
    }

    private handleApproveCheckboxClicked = (e:any):void => {
        const checkbox = this._mdcCheckboxes['appEditModalApproveAppCheckbox'];
        const btn = <HTMLButtonElement>document.getElementById('appEditModalApproveAppBtnSave');
        btn.disabled = !checkbox.checked;
    }

    private _getSelectedViewpropLanguage = (): string => {
        return this._mdcSelects['viewPropLanguageSelector'].value;
    };

    private _approvalStateAppBuilderAction(approvalChip: HTMLElement) {
        const necessaryData = `Appname = ${approvalChip.dataset.appname}\nProvider ID = ${approvalChip.dataset.providerId}`;
        const href = "mailto:cloud.support@d-velop.de?subject=" + escape(`Approval required for d.velop cloud App: ${approvalChip.dataset['appname']}`) +
            "&body=" + escape(`Your message (optional):\n\n\n--- Please do not change any values ---\n\n${necessaryData}\n\n--- Please do not change any values ---`);
        const reqFieldsMissing = !!document.getElementById("viewPropLanguageSelector_warningicon");
        approvalChip.addEventListener('click', () => {
            this._openMailtoDialog(necessaryData, href, reqFieldsMissing);
        });
    }

    private _changeAndSaveApprovalState = (cbSuccess?: () => void, cbError?:(message:string) => void):void => {
        const url = UrlHelpers.getLinkRelHref('self');
        const appName = UrlHelpers.getMetaData('appName');
        const currentApprovalState = UrlHelpers.getMetaData('approvalState') == "true";

        const data = {
            appName: appName,
            isApproved: !currentApprovalState,
        }

        const req = new Request(url, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/merge-patch+json'
            },
            body: JSON.stringify(data),
            credentials: 'same-origin'
        });

        fetch(req)
            .then((r:Response) => {
                if (r.status !== 200) {
                    r.text().then((text:string) => {
                        console.error(text);
                        throw `approval request failed with error code ${r.status} and message '${text}`;
                    });
                }
                if(cbSuccess) {
                    cbSuccess();
                }
                return
            })
            .catch((e:string) => {
                console.log(`Request to ${url} failed! Error: '${e}'`);
                if (cbError) {
                    cbError(e);
                }
            });
    };

    private _handleAppApproveDialogClosed = (e: dialog.MDCDialogCloseEventDetail) => {
        if (e.detail.action === 'save') {
            this._changeAndSaveApprovalState(() => {
                window.location.reload();
            }, message => {
                this._showErrorNotification('appEdit-notification', 'FAILED');
            });
        }

        const mdcCheckbox = <mdc.checkbox.MDCCheckbox>this._mdcCheckboxes['appEditModalApproveAppCheckbox'];
        mdcCheckbox.checked = false;

        const warningElement = <HTMLParagraphElement>document.getElementById('appEditModalApproveAppContentWarning');
        // if(warningElement) {
            warningElement.innerHTML = '';
        // }

        const dialog = this._mdcDialogs['appEditModalApproveApp'];
        dialog.unlisten('MDCDialog:closed', this._handleAppApproveDialogClosed);
    };

    private _handleListOfNewDependenciesAction = (e: list.MDCListActionEventDetail) => {
        if (e.detail.index > -1) {
            const btn = <HTMLButtonElement>document.getElementById('appEditModalAddAppDependency_add_button');
            btn.disabled = false;
        }
    };

    private _setMediaImages() {
        const mediaElements = document.getElementsByClassName('media-image-wrapper');
        for (let i = 0; i < mediaElements.length; i++) {
            const currentElement = <HTMLButtonElement>mediaElements[i];
            const bgImgUrl = currentElement.dataset.imageUrl;
            currentElement.setAttribute('style', 'background-image: url(' + bgImgUrl + ');');
        }
    }

    private _setSubscriptionNotification(visible: boolean) {
        const wrapper = document.getElementById('useSubscriptionNotificationMail_wrapper');
        const textfield = this._mdcTextFields["useSubscriptionNotificationMail_textfield"];
        if (!textfield || !wrapper) {
            console.log(`couldn't find ${!textfield ? "textfield" : "wrapper"}`);
        }
        if (!visible) {
            wrapper.style.display = 'none';
            return;
        }
        wrapper.style.display = 'block';
        if (textfield.value === "") {
            textfield.value = textfield.root_.dataset["supportmail"];
            textfield.focus();
        }
    }

    private _loadMaterial = () => {
        const topBar = document.getElementById("appEdit-top-bar");
        topAppBar.MDCTopAppBar.attachTo(topBar);

        const buttons = document.querySelectorAll('.mdc-button');
        Array.apply(null, buttons).forEach((button) => {
            ripple.MDCRipple.attachTo(button);
        });

        const textfields = document.querySelectorAll('.mdc-text-field');
        Array.apply(null, textfields).forEach((textfield) => {
            const mdcTextfield = textField.MDCTextField.attachTo(textfield);
            //take input or textarea id if wrapping div has no id itself
            if (!textfield.id) {
                const inputs = textfield.getElementsByTagName('input');
                if (inputs.length > 0 && inputs[0].id) {
                    this._mdcTextFields[inputs[0].id] = mdcTextfield;
                    return
                }
                const areas = textfield.getElementsByTagName('textarea');
                if (areas.length > 0 && areas[0].id) {
                    this._mdcTextFields[areas[0].id] = mdcTextfield;
                    return
                }
                return
            }
            this._mdcTextFields[textfield.id] = mdcTextfield;
        });

        const cc = document.querySelectorAll('.mdc-text-field-character-counter');
        Array.apply(null, cc).forEach((c) => {
            textField.MDCTextFieldCharacterCounter.attachTo(c);
        });

        const dialogs = document.querySelectorAll('.mdc-dialog');
        Array.apply(null, dialogs).forEach((d) => {
            const mdcDialog = dialog.MDCDialog.attachTo(d);
            if (d.id) {
                this._mdcDialogs[d.id] = mdcDialog;
            }
        });

        const bars = document.querySelectorAll('.mdc-snackbar');
        Array.apply(null, bars).forEach((b) => {
            snackbar.MDCSnackbar.attachTo(b);
        });

        const progressbars = document.querySelectorAll('.mdc-linear-progress');
        Array.apply(null, progressbars).forEach((progress) => {
            linearProgress.MDCLinearProgress.attachTo(progress);
        });

        const swList = document.querySelectorAll('.mdc-switch');
        Array.apply(null, swList).forEach((sw) => {
            switchControl.MDCSwitch.attachTo(sw);
        });

        const uLists = document.querySelectorAll('.mdc-list');
        Array.apply(null, uLists).forEach((uList) => {
            if (uList.id == 'appEditModalAddAppDependency_appList') {
                return;
            }
            const mdcList = list.MDCList.attachTo(uList);
            if (uList.id) {
                this._mdcLists[uList.id] = mdcList;
            }
        });

        const selects = document.querySelectorAll('.mdc-select');
        Array.apply(null, selects).forEach((s: HTMLElement) => {
            const mdcSelect = select.MDCSelect.attachTo(s);
            if (s.id == "viewPropLanguageSelector") {
                mdcSelect.listen('MDCSelect:change', this._onViewPropLanguageSelector_changed);
                this._showWarningIconInLanguageSelector();
                this._removeIconForSelectedText(mdcSelect);
            }
            if (s.id == "appEditModalAddDocument-type-select") {
                mdcSelect.listen('MDCSelect:change', this._handleDocumentTypeChanged);
            }
            this._mdcSelects[s.id] = mdcSelect;
        });

        const selectHelperTexts = document.querySelectorAll('.mdc-select-helper-text');
        Array.apply(null, selectHelperTexts).forEach((s: HTMLElement) => {
            const mdcSelectHelperText = select.MDCSelectHelperText.attachTo(s);
            if (s.id) {
                this._mdcSelectHelperTexts[s.id] = mdcSelectHelperText;
            }
        });

        const checkboxes = document.querySelectorAll('.mdc-checkbox');
        Array.apply(null, checkboxes).forEach((cb) => {
            const mdcCb = checkbox.MDCCheckbox.attachTo(cb);
            if (cb.id === 'appEditModalApproveAppCheckbox') {
                mdcCb.listen('click', this.handleApproveCheckboxClicked);
            } else if (cb.id === 'appEditModalAdministrateDependency-isSilentDependency') {
                mdcCb.listen('click', this.handleIsSilentDependencyCheckboxClicked);
            }
            if (cb.id) {
                this._mdcCheckboxes[cb.id] = mdcCb;
            }
        });

        const radioButtons = document.querySelectorAll('.mdc-radio');
        Array.apply(null, radioButtons).forEach((rb) => {
            const mdcRb = radio.MDCRadio.attachTo(rb);
            if (rb.id) {
                this._mdcRadioButtons[rb.id] = mdcRb;
            }
        });
    };

    private handleIsSilentDependencyCheckboxClicked = (e:any) => {
        console.debug(e);
        const checkbox = this._mdcCheckboxes['appEditModalAdministrateDependency-isSilentDependency'];
        const dialog = <HTMLDivElement>document.getElementById('appEditModalAdministrateDependency');
        if (checkbox.checked) {
            dialog.dataset.silentdependency = "0"
        } else {
            dialog.dataset.silentdependency = "1"
        }
    }

    private _showWarningIconInLanguageSelector = () => {
        const languageSelectorWarningIconId = 'viewPropLanguageSelector_warningicon';
        const languageSelector = document.getElementById('viewPropLanguageSelector');
        languageSelector.classList.remove('mdc-select--with-leading-icon');
        const languageItems = languageSelector.querySelectorAll('.mdc-list-item');
        let languageWarningExist = false;
        Array.apply(null, languageItems).forEach((item) => {
            for (let i = 0; i < item.children.length; i++) {
                let child = item.children[i];
                if (child.classList.contains('material-icons')) {
                    languageWarningExist = true;
                }
            }
        });
        if (languageWarningExist) {
            const anchor = languageSelector.querySelector('.mdc-select__anchor');
            for (let i = 0; i < anchor.children.length; i++) {
                let child = anchor.children[i];
                if (child.classList.contains('mdc-select__selected-text')) {
                    let warning = document.createElement('i');
                    warning.id = languageSelectorWarningIconId;
                    warning.classList.add('material-icons', 'mdc-select__icon');
                    warning.style.color = 'orange';
                    warning.setAttribute('tabindex', '0');
                    warning.setAttribute('role', 'button');
                    warning.innerText = 'warning';
                    anchor.insertBefore(warning, child);
                    languageSelector.classList.add('mdc-select--with-leading-icon');
                    break;
                }
            }
        }
    };

    private _removeIconForSelectedText = (mdcSelect: mdc.select.MDCSelect) => {
        const iconElement = mdcSelect.menu_.getOptionByIndex(mdcSelect.selectedIndex).querySelector(".material-icons");
        if (iconElement) {
            const icon = iconElement.innerText.trim();
            mdcSelect.leadingIconContent = icon;
            mdcSelect.selectedText_.innerText = mdcSelect.selectedText_.innerText.replace(icon, "").trim();
        }
    };

    private _showErrorNotification = (id: string, message: string) => {
        let div = document.getElementById(id);
        let bar: snackbar.MDCSnackbar = new snackbar.MDCSnackbar(div);
        bar.labelText = message;
        bar.closeOnEscape = true;
        bar.open();
    };

    private _reload = (url: string, anchorId: string) => {
        window.localStorage.setItem('scrollPageId', anchorId);
        window.location.href = url;
    };

    private _bindCollapsibles = () => {
        const collapsibles: collapsible[] = [
            {
                targets: ['viewprop-icon-button-start-edit-buttons', 'viewprop-icon-button-end-edit-buttons', 'viewprop-edit-section', 'viewprop-readonly-section'],
                buttons: ['viewprop-edit-start', 'viewprop-edit-cancel']
            },
            {
                targets: ['technical-details-icon-button-start-edit-buttons', 'technical-details-icon-button-end-edit-buttons', 'technical-details-edit-section', 'technical-details-readonly-section'],
                buttons: ['technical-details-edit-start', 'technical-details-edit-cancel']
            },
            {
                targets: ['pricing-start-edit-buttons', 'pricing-end-edit-buttons', 'pricing-edit-section', 'pricing-readonly-section'],
                buttons: ['pricing-edit-start', 'pricing-edit-cancel']
            }

        ];
        collapsibles.forEach((c) => {
            this._bindTargetsToButtons(c);
        });
    };

    private _setSaveButtonHandler = () => {
        (<HTMLButtonElement>document.getElementById('viewprop-edit-save')).addEventListener('click', this._saveViewprop);
        (<HTMLButtonElement>document.getElementById('technical-details-edit-save')).addEventListener('click', this._saveTechnicalDetails);

        const btnEditPricingSave = (<HTMLButtonElement>document.getElementById('pricing-edit-save'));
        if (btnEditPricingSave) {
            btnEditPricingSave.addEventListener('click', this._savePricingProp);
        }
    };

    private _initializeMediamanagerInputs = () => {
        //App Logo
        this._initializeMediamanagerForInputButton('general-info-logo-input', 'app-logo-input-button', (source) => {
            const logoUrl = <HTMLInputElement>document.getElementById('general-info-logo-url-input');
            logoUrl.setAttribute("value", source);
            FieldHandler.setFieldValue(logoUrl.id, source);
            this._saveGeneralInfo(null);
            this._reloadPageWithPageScroll(logoUrl.id);
        });
        //Document Upload
        this._initializeMediamanagerForInputButton('appEditModalEditScreenshot_filechooser', 'appEditModalAddDocument-upload-button', (source) => {
            const urlInput = <HTMLInputElement>document.getElementById('appEditModalEditScreenshot_document_url');
            urlInput.setAttribute("value", source);
            FieldHandler.setFieldValue(urlInput.id, source);
            this._validateDocumentDialog();
        });
    };

    private _reloadPageWithPageScroll = (id: string) => {
        window.localStorage.setItem('scrollPageId', id);
        this._reload(window.location.href, id);
    };

    private _reloadWithPositionScroll = (y: number) => {
        window.localStorage.setItem('scrollPosition', y.toString());
        window.location.reload();
    };

    private _scrollToPosition = (y: number) => {
        window.scrollTo(0, y);
    };

    private _scrollToView = (id: string, smooth: boolean = false) => {
        setTimeout(() => {
            document.getElementById(id).scrollIntoView((smooth ? {behavior: 'smooth'} : null));
        }, 300);
    };

    private _handleOpenDeleteModal = () => {
        let div = document.getElementById('appEditModalDelete');
        let md = new dialog.MDCDialog(div);
        md.listen('MDCDialog:closed', (e: dialog.MDCDialogCloseEventDetail) => {
            this._handleDeleteModalAction(md, e);
        });
        md.open();
    };

    private _handleDeleteModalAction = (md: dialog.MDCDialog, e: dialog.MDCDialogCloseEventDetail) => {
        if (e.detail.action === 'delete') {
            this._deleteApp();
        }
        md.unlisten('MDCDialog:closed');
    };

    private _openMailtoDialog = (appData, mailToHref: string, showWarning: boolean = false) => {
        const modalId = 'appEditModalNoMailtoHandler';
        const div = document.getElementById(modalId);
        const noMailtoData = <HTMLParagraphElement>document.getElementById('appEditModalNoMailtoHandlerData');
        noMailtoData.innerText = appData;
        const warningSection = <HTMLParagraphElement>document.getElementById('appEditModalRequiredFieldsMissing');
        warningSection.style.visibility = showWarning ? 'visible' : 'hidden';
        const md = new dialog.MDCDialog(div);
        md.listen('MDCDialog:closing', (e: dialog.MDCDialogCloseEventDetail) => {
            if (e.detail.action === 'request') {
                window.open(mailToHref);
            }
        });
        this._mdcDialogs[modalId] = md;
        md.open();
    };

    private _deleteApp = () => {
        let url = UrlHelpers.getLinkRelHref('self');
        let xhr = new XMLHttpRequest();
        xhr.open('DELETE', url);
        xhr.setRequestHeader('accept', 'application/json');
        xhr.onload = () => {
            if (xhr.status === 204) {
                this._dapi.dispatchResourceEvent({
                    name: 'deleted',
                    uri: app_edit_deleted //'/center/app/deleted'
                });
                if (this._dapi.isMainResource()) {
                    this._dapi.closeResourceIfMainResource();
                } else {
                    location.href = UrlHelpers.getLinkRelHref('parent');
                }
            } else if (xhr.status === 404) {
                console.error("App not found: " + xhr.responseText);
                this._showErrorNotification(this._snackbarElementId, this._i18n.getTranslation('app_edit_nofication_delete_failed_appnotfound_message'));
            } else if (xhr.status === 409) {
                console.error('App is in use: ' + xhr.responseText);
                this._showErrorNotification(this._snackbarElementId, this._i18n.getTranslation('app_edit_nofication_delete_failed_appisinuse_message'));
            } else {
                console.error(xhr.responseText);
                this._showErrorNotification(this._snackbarElementId, this._i18n.getTranslation('app_edit_nofication_delete_failed_unknownerror_message'));
            }
        };
        xhr.send();
    };

    private _saveAppData = (data: patchAppRequest<appRequestViewProp | appRequestTechnicalDetails | appRequestGeneral | appRequestDocument | appPricingProp>, isModal: boolean, callbackSuccess: () => void, callbackError: (errorstatus: number, errormessage: string) => void): void => {
        if (data !== null) {
            const url = UrlHelpers.getLinkRelHref('self');
            const req = new Request(url, {
                method: 'PATCH',
                headers: {
                    'accept': 'application/json'
                },
                body: JSON.stringify(data)
            });
            fetch(req, {credentials: "same-origin"})
                .then((response) => {
                    if (response.ok) {
                        callbackSuccess();
                    } else {
                        response.json().then(value => {
                            callbackError(response.status, JSON.stringify(value));
                        });
                    }
                })
                .catch(error => {
                    console.error(error);
                    callbackError(999, error);
                });
            return;
        }
        console.log('Missing data - cannot make request!');
    };

    private _getPatchAppRequest = (action: PatchAppAction, data: appRequestViewProp | appRequestTechnicalDetails | appRequestGeneral | appRequestDocument | appPricingProp): patchAppRequest<appRequestViewProp | appRequestTechnicalDetails | appRequestGeneral | appRequestDocument | appPricingProp> => {
        return {
            appName: UrlHelpers.getMetaData('appName'),
            action: action,
            data: data
        } as patchAppRequest<typeof data>;
    };

    private _bindTargetsToButtons = (collapsibles: collapsible) => {
        collapsibles.buttons.forEach((buttonId: string) => {
            const button = <HTMLButtonElement>document.getElementById(buttonId);
            if (!button) {
                console.log('Cannot find button with id: ', button);
                return;
            }
            button.addEventListener('click', (e: Event) => {
                this._toggleTargets(collapsibles.targets);
                for (const key in this._mdcTextFields) {
                    const tf = this._mdcTextFields[key];
                    const value = tf.value.trim();
                    this._setMDCTextFieldValue(tf, value.length > 0 ? tf.value : value);
                }

                const target = <HTMLElement>e.target;
                const attKey = 'data-scrollTo';
                if (target.hasAttribute(attKey) && target.getAttribute(attKey) != "") {
                    this._scrollToView(button.getAttribute(attKey), true);
                }
            });
        });
    };

    private _setMDCTextFieldValue = (tf: textField.MDCTextField, value: string) => {
        tf.value = value;
    };

    private _toggleTargets = (targetIds: string[]) => {
        targetIds.forEach((t) => {
            this._toggleCollapse(t);
        });
    };

    private _toggleCollapse = (id: string) => {
        document.getElementById(id).classList.toggle('cc-collapse');
    };

    private _onViewPropLanguageSelector_changed = (e: any) => {
        let selectedLanguageKey = e.detail.value;
        let mdcList = this._mdcSelects[e.target.id];
        this._showWarningIconInLanguageSelector();
        this._removeIconForSelectedText(mdcList);
        let url = UrlHelpers.setQueryParameter(location.href, 'translation', selectedLanguageKey);
        this._reload(url, e.target.id);
    };

    private _saveTechnicalDetails = (e: any) => {
        const detailsData = this._getTechnicalDetailsData();
        if (!detailsData) {
            return;
        }
        const data = this._getPatchAppRequest(PatchAppAction.TECHNICAL, detailsData);
        this._saveAppData(data, false, () => {
            this._reloadPageWithPageScroll(e.target.id);
        }, (errorstatus, errormessage) => {
            console.error(`save technical details failed with status ${errorstatus}: ${errormessage}`);
            let err: errorDto = ErrorHandler.parseError(errormessage);
            if (err === null) {
                this._showErrorNotification(this._snackbarElementId, errormessage);
            } else {
                let msg = err.description;
                if (msg.startsWith("decode technical dto failed.")) {
                    msg = this._i18n.getTranslation('app_edit_viewProp_error_request_wrong_msg');
                } else if (msg.startsWith("Last app view property cannot be deleted")) {
                    msg = this._i18n.getTranslation('app_edit_viewProp_error_cannot_delete_last_translation_msg');
                } else if (msg.startsWith('App dependencies not found:')) {
                    msg = this._i18n.getTranslation('app_edit_technical_error_dependencies_not_found');
                }
                this._showErrorNotification(this._snackbarElementId, msg);
            }
        });
    };

    private _saveViewprop = (e: any) => {
        const viewPropData = this._getViewPropData();
        if (!viewPropData) {
            return
        }
        const data = this._getPatchAppRequest(PatchAppAction.VIEWPROP, viewPropData);
        this._saveAppData(data, false, () => {
            this._dapi.dispatchResourceEvent({
                name: 'changed',
                uri: app_edit_changed_saved
            });
            this._reloadPageWithPageScroll(e.target.id);
        }, (errorstatus, errormessage) => {
            console.error(`save View Properties failed with status ${errorstatus}: ${errormessage}`);
            let err: errorDto = ErrorHandler.parseError(errormessage);
            if (err === null) {
                this._showErrorNotification(this._snackbarElementId, errormessage);
            } else {
                let msg = err.description;
                if (msg.startsWith("decode view property failed.")) {
                    msg = this._i18n.getTranslation('app_edit_viewProp_error_request_wrong_msg');
                }
                this._showErrorNotification(this._snackbarElementId, msg);
            }
        });
    };

    private _savePricingProp = () => {
        const pricingPropData = this._getPricingPropData();
        if (!pricingPropData) {
            return
        }
        const data = this._getPatchAppRequest(PatchAppAction.BILLING, pricingPropData);
        this._saveAppData(data, false, () => {
            this._dapi.dispatchResourceEvent({
                name: 'changed',
                uri: app_edit_changed_saved
            });
            this._reloadPageWithPageScroll("appEdit-pricing-card");
        }, (errorstatus, errormessage) => {
            console.error(`save billing details failed with status ${errorstatus}: ${errormessage}`);
            let err: errorDto = ErrorHandler.parseError(errormessage);
            if (err === null) {
                this._showErrorNotification(this._snackbarElementId, errormessage);
            } else {
                let msg = err.description;
                if (msg.startsWith("decode view property failed.")) {
                    msg = this._i18n.getTranslation('app_edit_viewProp_error_request_wrong_msg');
                }
                this._showErrorNotification(this._snackbarElementId, msg);
            }
        });
    };

    private _patchLegalWithAction = (action: PatchAppAction, data: appRequestDocument, cbSuccess: () => void, cbError: (string) => void) => {
        const requestData = this._getPatchAppRequest(action, data);
        this._saveAppData(requestData, false, cbSuccess, cbError);
    };

    private _updateListElement = (listElement: HTMLLIElement) => {
        const link = listElement.getAttribute(DataAttributes.VALUE);
        const key = listElement.getAttribute(DataAttributes.KEY);
        const required = ['generalTerms', 'sla', 'performanceSpecification'];
        if (required.indexOf(key) < 0) {
            if (link.length < 1) {
                listElement.remove();
            }
            return;
        }
        const deleteButton = <HTMLButtonElement>listElement.getElementsByClassName('document-delete')[0];
        const copyUrlButton = <HTMLButtonElement>listElement.getElementsByClassName('document-copyurl')[0];
        deleteButton.disabled = link.length < 1;
        copyUrlButton.disabled = link.length < 1;
    };

    private _getParentFromEvent = (e: Event): HTMLElement => {
        const targetParent = (<HTMLElement>e.target).parentElement;
        if (!targetParent || !targetParent.hasAttribute(DataAttributes.KEY) || !targetParent.hasAttribute(DataAttributes.VALUE)) {
            console.warn('Cannot find parent of target or data-attribute: ', (<HTMLElement>e.target).id);
            return null;
        }
        return targetParent;
    };

    private _saveLegal = () => {
        this._saveLegalFromDialog();
    };

    private _saveLegalFromDialog = () => {
        const select = this._mdcSelects['appEditModalAddDocument-type-select'];
        const title = this._mdcTextFields['appEditModalAddDocument-title'];
        const upload = <HTMLInputElement>document.getElementById('appEditModalEditScreenshot_document_url');
        if (!(title && upload && select)) {
            return;
        }
        const language = UrlHelpers.getMetaData('language');
        const data: appRequestDocument = {
            name: title.value,
            uploadTime: null,
            type: select.value,
            link: upload.value,
            language: language //this._getLanguageFromSelector()
        };
        this._patchLegalWithAction(PatchAppAction.ADDDOCUMENT, data, () => {
            this._reloadWithPositionScroll(window.scrollY);
        }, this._showUploadFailed);
    };

    private _openDeleteLegalDialog = (btnEvent: Event) => {
        let div = document.getElementById('appEditModalDeleteLegalDocument');
        let md = new dialog.MDCDialog(div);
        md.listen('MDCDialog:closed', (e: dialog.MDCDialogCloseEventDetail) => {
            if (e.detail.action === 'yes') {
                this._deleteLegal(btnEvent);
            }
        });
        md.open();
    };

    private _deleteLegal = (e: Event) => {
        const targetParent = this._getParentFromEvent(e);
        if (!targetParent) {
            return
        }
        let type = targetParent.getAttribute(DataAttributes.KEY);
        const docTypeMiscellaneous = 'misc';
        type = type.startsWith(docTypeMiscellaneous) ? docTypeMiscellaneous : type;
        let name: string = null;
        const language = UrlHelpers.getMetaData('language');
        if (type == docTypeMiscellaneous) {
            name = targetParent.getAttribute(DataAttributes.NAME);
            if (!name) {
                return;
            }
        }
        const data: appRequestDocument = {
            link: "",
            type: type,
            uploadTime: null,
            name: name,
            language: language // this._getLanguageFromSelector()
        };
        this._patchLegalWithAction(PatchAppAction.REMOVEDOCUMENT, data, () => {
            targetParent.setAttribute(DataAttributes.VALUE, "");
            this._reloadWithPositionScroll(window.scrollY);
        }, this._showDeleteFailed) //Callbacks
    };

    private _copyDocumentUrl = (e: Event) => {
        const parent = this._getParentFromEvent(e);
        if (!parent) {
            return
        }
        if (parent.getAttribute(DataAttributes.VALUE).length > 0) {
            let path = parent.getAttribute(DataAttributes.VALUE);
            let uri = `${location.origin}${path}`;
            if (path.startsWith("https://") || path.startsWith("http://")) {
                uri = path
            }
            const dummy = <HTMLInputElement>document.createElement("input");
            dummy.setAttribute('readonly', '');
            dummy.style.position = 'absolute';
            dummy.style.left = '-9999px';
            document.body.appendChild(dummy);
            dummy.value = uri;
            dummy.select();
            document.execCommand('copy');
            document.body.removeChild(dummy);
        }
    };

    private _saveGeneralInfo = (e: any) => {
        const data = this._getPatchAppRequest(PatchAppAction.GENERAL, this._getGeneralData());
        this._saveAppData(data, false, () => {
            this._dapi.dispatchResourceEvent({
                name: 'changed',
                uri: app_edit_changed_saved,
            });
            if (e !== null) {
                this._reloadPageWithPageScroll(e.target.id);
            }
        }, (errorstatus, errormessage) => {
            let errMsg = ErrorHandler.parseError(errormessage);
            this._showErrorNotification(this._snackbarElementId, errMsg.description);
        });
    };

    private _initializeMediamanagerForInputButton = (inputId: string, buttonId: string, callback: inputButtonCallback) => {
        const fileInput = <HTMLInputElement>document.getElementById(inputId);
        (<HTMLButtonElement>document.getElementById(buttonId)).addEventListener('click', () => {
            this._mediaButtonHandler(fileInput);
        });
        new MediamanagerFileInputHandler(fileInput, (source) => {
            callback(source);
        });
    };

    private _mediaButtonHandler = (input: HTMLInputElement) => {
        input.click();
    };

    private _getTechnicalDetailsData = (): appRequestTechnicalDetails => {
        let dependencies = FieldHandler.getMultipleValue('technical-details-dependencies-input', ',');
        const trimmed = dependencies.map((d) => {
            return d.trim();
        });
        let isSubscribable = FieldHandler.getBooleanValue('switchIsBackgroundApp');
        const endpoint = this._mdcTextFields['technical-details-hostname-input'];

        //TODO: rework for mdc select collection map
        const mdcInputs = <mdcInputValidation[]>[
            {
                input: endpoint,
                minLength: 1
            }
        ];
        const notificationCheckbox = this._mdcCheckboxes["useSubscriptionNotificationMail_checkbox"];
        const notificationTextfield = this._mdcTextFields["useSubscriptionNotificationMail_textfield"];
        if (notificationCheckbox.checked) {
            if (notificationTextfield) {
                mdcInputs.push({
                    input: notificationTextfield,
                    minLength: 6
                });
            }
        }

        if (!this._validateMDCTextFields(mdcInputs)) {
            return null;
        }

        if (!isEndpointValid(endpoint.value)) {
            console.warn(`invalid app endpoint found: ${endpoint.value}`);
        }

        const data = {
            endpoint: endpoint.value,
            dependencies: trimmed,
            isBackground: !isSubscribable,
            subscriptionNotificationMail: notificationCheckbox.checked ? notificationTextfield.value : ""
        } as appRequestTechnicalDetails;
        console.log(data);
        return data;
    };

    private _getViewPropData = (): appRequestViewProp => {
        const currentLanguage = this._mdcSelects['viewPropLanguageSelector'].value;
        const displayname = this._mdcTextFields['viewprop-displayname-input'];
        const mdcInputs = <mdcInputValidation[]>[
            {
                input: displayname,
                minLength: 1
            }
        ];

        if (!this._validateMDCTextFields(mdcInputs)) {
            return null;
        }
        return {
            displayName: displayname.value,
            summary: FieldHandler.getFieldValue('viewprop-summary-input'),
            language: currentLanguage
        } as appRequestViewProp;
    };

    private _getPricingPropData = (): appPricingProp => {
        const currentLanguage = this._mdcSelects['viewPropLanguageSelector'].value;
        const billingMarkup = this._mdcTextFields['pricing-markup-textarea'];
        return {
            billingMarkup: billingMarkup.value,
            language: currentLanguage,
            useMetricDataForDescription: false, //field is deprecated
            priceListAnnotation: "" //field is deprecated
        } as appPricingProp;
    };

    private _getGeneralData = (): appRequestGeneral => {
        return {
            icon: FieldHandler.getFieldValue('general-info-logo-url-input'),
        } as appRequestGeneral;
    };

    private _validateMDCTextFields = (input: mdcInputValidation[]): boolean => {
        input.forEach((i) => {
            if (!this._validateMDCTextField(i.input, i.minLength)) {
                return false
            }
        });
        return true;
    };

    private _validateMDCTextField = (input: textField.MDCTextField, minlength: number): boolean => {
        if (!input) {
            return false
        }
        const valid = this._isValidInputValue(input.value, minlength);
        input.valid = valid;
        return valid;
    };

    private _isValidInputValue = (value: string, minlength: number): boolean => {
        value = value ? value.trim() : value;
        return (value && value.length >= minlength);
    };

    private _copySecret = (e: any) => {
        let clickedButton = e.currentTarget;
        let copyElementId = clickedButton.dataset.copy;
        let range = document.createRange();
        let div = <HTMLDivElement>document.getElementById(copyElementId);
        let isInvisible = false;
        if (div.style.display === 'none') {
            isInvisible = true;
            div.style.removeProperty('display');
            div.style.position = 'absolute';
            div.style.left = '-1000px';
            div.style.top = '-1000px';
        }
        range.selectNodeContents(div);
        let selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        try {
            document.execCommand('copy', false, null);
        } catch (e) {
            console.error(e);
        } finally {
            if (isInvisible) {
                div.style.display = 'none';
                div.style.removeProperty('position');
                div.style.removeProperty('left');
                div.style.removeProperty('top');
            }
        }
    };

    private _handleOpenGenerateSecretModal = () => {
        let btnCopyNewSecret = <HTMLButtonElement>document.getElementById('btnCopyNewSecret');
        if (btnCopyNewSecret) {
            btnCopyNewSecret.addEventListener('click', this._copySecret);
        }
        let btnGenerate = <HTMLButtonElement>document.getElementById('btnGenerateNewSecret');
        if (btnGenerate) {
            btnGenerate.addEventListener('click', this._handleButtonGenerateNewSecretAction);
        }
        let div = document.getElementById('appEditModalGenerateSecret');
        let md = new dialog.MDCDialog(div);
        md.listen('MDCDialog:opening', () => {
            let div = <HTMLDivElement>document.getElementById('newAppSecret');
            div.innerHTML = `<em>${this._i18n.getTranslation("app_edit_create_new_app_secret_helpertext_message")}</em>`
        });
        md.listen('MDCDialog:closed', () => {
            let btnCopyNewSecret = <HTMLButtonElement>document.getElementById('btnCopyNewSecret');
            if (btnCopyNewSecret) {
                btnCopyNewSecret.removeEventListener('click', this._copySecret);
                btnCopyNewSecret.disabled = true;
            }
            let btnGenerate = <HTMLButtonElement>document.getElementById('btnGenerateNewSecret');
            if (btnGenerate) {
                btnGenerate.removeEventListener('click', this._handleButtonGenerateNewSecretAction);
            }
        });
        md.open();
    };

    private _handleButtonGenerateNewSecretAction = (e: any) => {
        e.preventDefault();
        let progressdiv = document.getElementById('generateprogress');
        progressdiv.classList.add('mdc-linear-progress--indeterminate');
        let progressbar: linearProgress.MDCLinearProgress = new linearProgress.MDCLinearProgress(progressdiv);
        let txtSecret = <HTMLDivElement>document.getElementById('newAppSecret');

        progressbar.open();
        txtSecret.innerText = this._i18n.getTranslation('app_edit_create_new_app_secret_processing_message');

        let url = UrlHelpers.getLinkRelHref('self');
        let xhr = new XMLHttpRequest();
        xhr.open("POST", url);
        xhr.setRequestHeader("accept", "application/json");
        xhr.onload = () => {
            if (xhr.status === 200) {
                txtSecret.innerText = xhr.responseText.replace(/^"(.*)"$/, '$1');
                let copyBtn = <HTMLButtonElement>document.getElementById('btnCopyNewSecret');
                copyBtn.removeAttribute('disabled');
            } else {
                let err = <HTMLDivElement>document.getElementById('newAppSecret');
                err.innerText = this._i18n.getTranslation('app_edit_generatesecret_failed_message'); //"Es trat ein Fehler beim Generieren des App Secret auf.";
                console.error(xhr.responseText);
            }
            progressbar.close();
        };
        xhr.send();
    };

    private _showNotification = (id: string, message: string, callback?: Function) => {
        let div = <HTMLDivElement>document.getElementById(id);
        if (div) {
            let bar = new mdc.snackbar.MDCSnackbar(div);
            bar.labelText = message;
            bar.closeOnEscape = true;
            if (callback) {
                bar.listen('MDCSnackbar:closed', callback)
            }
            bar.open();
        }
    };

    private _showUploadFailed = (message: string) => {
        if (message) {
            console.warn("Upload request failed. Message: ", message);
        }
        this._showNotification(this._snackbarElementId, this._i18n.getTranslation('config_terms_notificaton_failed'));
    };

    private _showDeleteFailed = (message: string) => {
        if (message) {
            console.warn("Delete request failed. Message: ", message);
        }
        this._showNotification(this._snackbarElementId, this._i18n.getTranslation('config_terms_notificaton_failed_delete'))
    };

    private _handleDocumentTypeChanged = (e: mdc.select.MDCSelectEvent) => {
        let selectedType = e.detail.value;
        const currentLanguage = i18n.Language.parse(this._getSelectedViewpropLanguage());
        switch (selectedType) {
            case "generalTerms":
                this._documentTypeChanged(selectedType, this._i18n.getTranslation("app_page_legal_type_general_terms", currentLanguage), true);
                break;
            case "performanceSpecification":
                this._documentTypeChanged(selectedType, this._i18n.getTranslation("app_page_legal_type_performance_specifications", currentLanguage), true);
                break;
            case "sla":
                this._documentTypeChanged(selectedType, this._i18n.getTranslation("app_page_legal_type_sla", currentLanguage), true);
                break;
            case "privacyStatement":
                this._documentTypeChanged(selectedType, this._i18n.getTranslation("app_page_legal_type_privacy_statement", currentLanguage), true);
                break;
            case "orderProcessingAgreements":
                this._documentTypeChanged(selectedType, this._i18n.getTranslation("app_page_legal_type_order_processing_agreement", currentLanguage), true);
                break;
            case "subprocessorInformation":
                this._documentTypeChanged(selectedType, this._i18n.getTranslation("app_page_legal_type_subprocessor_information", currentLanguage), true);
                break;
            default:
                this._documentTypeChanged(selectedType, "", false);
        }
        this._validateDocumentDialog();
    };

    private _documentTypeChanged = (selectedType: string, title: string, hideTitle: boolean) => {
        // set title
        const titleInput = this._mdcTextFields['appEditModalAddDocument-title'];
        titleInput.value = title;
        titleInput.disabled = hideTitle; //Hide or disable?
        titleInput.layout();
        const select = this._mdcSelects['appEditModalAddDocument-type-select'];
        const anchor = <HTMLAnchorElement>document.getElementById('appEditModalAddDocument-templates-anchor');
        if (selectedType == 'misc') {
            anchor.hidden = true;
            return
        } else {
            anchor.hidden = false;
        }
        const templateString = select.root_.dataset.templates;
        if (!templateString) {
            console.log("cannot find template object");
            return;
        }
        const templateJson = JSON.parse(templateString);
        const docKeysToTemplateKeys = {
            "generalTerms": "GeneralTerms",
            "performanceSpecification": "PerformanceSpecification",
            "sla": "SLA",
            "privacyStatement": "PrivacyStatement",
            "orderProcessingAgreements": "OrderProcessingAgreements"
        };
        anchor.href = templateJson[docKeysToTemplateKeys[selectedType]];
    };

    private _showDocumentDialog = (preselectedIndex: number = 0) => {
        const dialog = this._mdcDialogs['appEditModalAddDocument'];
        dialog.listen('MDCDialog:closed', (e: dialog.MDCDialogCloseEventDetail) => {
            if (e.detail.action == 'save') {
                this._saveLegal();
            }
            dialog.unlisten('MDCDialog:closed');
        });
        dialog.open();
        this._resetDocumentUploadDialogWithPreselectedType(preselectedIndex);
    };

    private _handleSearchWithinNewAppDependencyList = (e: Event) => {
        const searchValue = (<HTMLInputElement>e.currentTarget).value;
        this._searchAppDependency(searchValue);
    }

    private _searchAppDependency = (searchValue: string) => {
        let items = document.querySelectorAll('.appEditModal-NewAppDependency');
        items.forEach((item: HTMLLIElement) => {
            if (searchValue.isNullOrEmpty()) {
                item.classList.remove('hide-element');
            } else if (item.dataset.appname.toLowerCase().includes(searchValue.toLowerCase())) {
                item.classList.remove('hide-element');
            } else if (item.dataset.appdisplayname.toLowerCase().includes(searchValue.toLowerCase())) {
                item.classList.remove('hide-element');
            } else {
                item.classList.add('hide-element');
            }
        });
    }

    private _showDependencyDialog = () => {
        const dialogAppDepLocal = this._mdcDialogs['appEditModalAddAppDependency'];

        const btnAddInDialog = <HTMLButtonElement>document.getElementById('appEditModalAddAppDependency_add_button');
        btnAddInDialog.disabled = true;

        this._searchAppDependency("");

        dialogAppDepLocal.open();
    };

    private _handleDependencyDialogClosed = (evt: dialog.MDCDialogCloseEventDetail) => {
        try {
            if (evt.detail.action == 'add') {
                this._checkSelectedAppDepIsApproved();
            } else if (evt.detail.action == 'deprequest') {
                const list = this._mdcLists['appEditModalAddAppDependency_appList'];
                const selectedIndex = <number>list.selectedIndex;
                const item = <HTMLLIElement>list.listElements[selectedIndex];
                const dependency = item.dataset.appname;

                const dialogDiv = <HTMLDivElement>document.getElementById('appEditModalAddDependencyRequest');
                dialogDiv.dataset.dependency = dependency;

                this._openAddDependencyRequestDialog();
            }
        } catch (e) {
            console.debug(e);
        }

        const listId = 'appEditModalAddAppDependency_appList';
        const mdclist = this._mdcLists[listId];
        if (mdclist) {
            mdclist.destroy();
        }
        let ul = document.getElementById(listId);
        ul.innerHTML = '';

        const searchField = <HTMLInputElement>document.getElementById('appEditModalAddAppDependency_searchField-input');
        searchField.value = '';
    };

    private _openAddDependencyRequestDialog = () => {
        const appEditModalAddDependencyRequest = 'appEditModalAddDependencyRequest';
        const dialog = this._mdcDialogs[appEditModalAddDependencyRequest];
        if (!dialog) {
            console.info('add dependency request dialog not loaded');
            return
        }

        const dialogDiv = <HTMLDivElement>document.getElementById(appEditModalAddDependencyRequest);

        const dependencyToAdd = dialogDiv.dataset.dependency;
        const currentApp = dialogDiv.dataset.appname;
        const providerId = dialogDiv.dataset.providerId;

        const data = `Appname = ${currentApp}\nProvider ID = ${providerId}\nDependency to add = ${dependencyToAdd}`;
        const contentData = <HTMLParagraphElement>document.getElementById('appEditModalAddDependencyRequest_data');
        contentData.innerText = data;

        dialog.listen('MDCDialog:closed', this._handleAddDependencyRequestDialogClosed);
        dialog.open();
    };

    private _handleAddDependencyRequestDialogClosed = (evt: dialog.MDCDialogCloseEventDetail) => {
        const appEditModalAddDependencyRequest = 'appEditModalAddDependencyRequest';
        const dialog = this._mdcDialogs[appEditModalAddDependencyRequest];
        dialog.unlisten('MDCDialog:closed', this._handleAddDependencyRequestDialogClosed);

        const dialogDiv = <HTMLDivElement>document.getElementById(appEditModalAddDependencyRequest);

        if (evt.detail.action == 'request') {
            const dependencyToAdd = dialogDiv.dataset.dependency;
            const currentApp = dialogDiv.dataset.appname;
            const providerId = dialogDiv.dataset.providerId;

            const data = `Appname = ${currentApp}\nProvider ID = ${providerId}\nDependency to add = ${dependencyToAdd}`;
            const href = "mailto:cloud.support@d-velop.de?subject=" + escape(`add dependency request for app ${currentApp}`) +
                "&body=" + escape(`Your message (optional):\n\n\n--- Please do not change any values ---\n\n${data}\n\n--- Please do not change any values ---`);
            window.open(href);
        }
        dialogDiv.dataset.dependency = "";
    };

    private _loadAppList = (cbSuccess: (dto: appDependencyList) => void, cbError: (e: string) => void) => {
        const url = UrlHelpers.getLinkRelHref('appListForAppDependencies');
        let req = new Request(url, {
            method: 'get',
            credentials: 'same-origin'
        });
        fetch(req)
            .then((r: Response) => {
                if (r.status !== 200) {
                    throw `request failed with http status ${r.status}`;
                }
                return r.json();
            })
            .then((dto: appDependencyList) => {
                cbSuccess(dto);
            })
            .catch((e: string) => {
                cbError(e);
            });
    };

    private _renderAppListIntoAppDependencyDialog = (dto: appDependencyList) => {
        console.info(`load ${dto.apps.length} apps...`);
        const listId = 'appEditModalAddAppDependency_appList';
        let existingMdcList = this._mdcLists[listId];
        if (existingMdcList) {
            existingMdcList.destroy();
        }

        const currentAppDependecies = this._getCurrentAppDependencies();
        const ul = document.getElementById(listId);
        const mainAppName = UrlHelpers.getMetaData('appName');

        dto.apps.forEach((item: appDependencyItem) => {
            if (mainAppName !== item.appName && !currentAppDependecies.includes(item.appName)) {
                if (item.isApproved) {
                    ul.insertAdjacentHTML('beforeend', `<li class="mdc-list-item appEditModal-NewAppDependency" data-appname="${Escaping.escapeHtml(item.appName)}" data-isapproved="${item.isApproved}" data-appdisplayname="${Escaping.escapeHtml(item.displayName)}" role="option">
                            <span class="mdc-list-item__ripple"></span>
                            <span class="mdc-list-item__text">
                                <span class="mdc-list-item__primary-text">${Escaping.escapeHtml(item.displayName)}</span>
                                <span class="mdc-list-item__secondary-text">${Escaping.escapeHtml(item.providerName)}</span>
                            </span>
                        </li>`);
                } else {
                    ul.insertAdjacentHTML('beforeend', `<li class="mdc-list-item appEditModal-NewAppDependency" data-appname="${Escaping.escapeHtml(item.appName)}" data-isapproved="${item.isApproved}" data-appdisplayname="${Escaping.escapeHtml(item.displayName)}" role="option">
                            <span class="mdc-list-item__ripple"></span>
                            <span class="mdc-list-item__text">
                                <span class="mdc-list-item__primary-text">${Escaping.escapeHtml(item.displayName)} (${this._i18n.getTranslation('app_edit_modal_addAppDep_info_appNotApproved_label')})</span>
                                <span class="mdc-list-item__secondary-text">${Escaping.escapeHtml(item.providerName)}</span>
                            </span>
                        </li>`);
                }
            } else {
                console.debug(`${item.appName} already in app dependency list.`);
            }
        });
        const mdcListNew = list.MDCList.attachTo(ul);
        mdcListNew.singleSelection = true;
        mdcListNew.listen('MDCList:action', this._handleListOfNewDependenciesAction);
        this._mdcLists[listId] = mdcListNew;
        this._showDependencyDialog();
    };

    private _checkSelectedAppDepIsApproved = () => {
        const list = this._mdcLists['appEditModalAddAppDependency_appList'];
        const selectedIndex = <number>list.selectedIndex;
        const item = <HTMLLIElement>list.listElements[selectedIndex];
        const isApproved = (item.dataset.isapproved == "true");
        if (isApproved) {
            this._addSelectedDependency(item);
        } else {
            this._showAppDepNotApprovedDialog(item);
        }
    };

    private _showAppDepNotApprovedDialog = (htmlItem: HTMLLIElement) => {
        const displayName = htmlItem.dataset.appdisplayname;
        const mainAppName = UrlHelpers.getMetaData('appName');

        const translationText = this._i18n.getTranslation('app_edit_modal_appNotApproved_text');
        let message = i18n.replaceTranslationPlaceholder(translationText, '{appDepName}', displayName);
        message = i18n.replaceTranslationPlaceholder(message, '{appDepName}', displayName);
        message = i18n.replaceTranslationPlaceholder(message, '{currentAppName}', mainAppName);
        // let message = `APP "${displayName}" IS NOT APPROVED. DO YOU REALY WANT TO THE APP AS A DEPENDENCY?`;
        const messageDiv = <HTMLDivElement>document.getElementById('appEditModalDependencyNotApproved-content');
        messageDiv.innerText = message;

        const dialogUnapprovedApps = this._mdcDialogs['appEditModalDependencyNotApproved'];

        const handler = (e: dialog.MDCDialogCloseEventDetail) => {
            const messageDiv = <HTMLDivElement>document.getElementById('appEditModalDependencyNotApproved-content');
            messageDiv.innerText = '';

            if (e.detail.action === 'ok') {
                this._addSelectedDependency(htmlItem);
            }

            dialogUnapprovedApps.unlisten('MDCDialog:closed', handler);
        }

        dialogUnapprovedApps.listen('MDCDialog:closed', handler);
        dialogUnapprovedApps.open();
    };

    private _addSelectedDependency = (htmlItem: HTMLLIElement) => {
        const appName = htmlItem.dataset.appname;

        console.info(`you have select ${appName} to add to dependencies`);

        this._addNewDependency(appName, () => {
            this._appendNewAddedApp(appName);
            this._addDependencyToULElement(appName, "none",true);
        });
    };

    private _getCurrentAppDependencies = (): Array<string> => {
        const appDepElements = document.querySelectorAll('.app-dependency');
        let currentAppDependencies = new Array<string>();
        appDepElements.forEach((item) => {
            const depAppName = item.getAttribute('data-appname');
            if (!depAppName.isNullOrEmpty()) {
                currentAppDependencies.push(depAppName);
            }
        });
        return currentAppDependencies;
    };

    private _handleRemoveDependency = (appName: string) => {
        if (!appName) {
            console.debug('handleRemoveDependencySecurityCheck: appName empty');
            return;
        }
        const mainAppName = UrlHelpers.getMetaData('appName');

        let message = this._i18n.getTranslation('app_edit_modal_removeAppDependency_text');
        message = i18n.replaceTranslationPlaceholder(message, '{depAppName}', appName);
        message = i18n.replaceTranslationPlaceholder(message, '{appName}', mainAppName);

        const dialogContent = <HTMLDivElement>document.getElementById('appEditModalRemoveDependency-content');
        dialogContent.innerText = message;

        const dialog = this._mdcDialogs['appEditModalRemoveDependency'];
        const handler = (e: dialog.MDCDialogCloseEventDetail) => {
            if (e.detail.action === 'remove') {
                this._removeDependency(appName);
            }
            dialog.unlisten('MDCDialog:closed', handler)
        }

        dialog.listen('MDCDialog:closed', handler);
        dialog.open();
    };

    private _showOrHideNoDependencyMessage = () => {
        const hide = 'hide-element';
        const noDependenyDiv = <HTMLDivElement>document.getElementById('app-has-no-dependencies');
        const foundAppDependency = this._getCurrentAppDependencies();
        if (foundAppDependency.length > 0) {
            noDependenyDiv.classList.add(hide);
        } else {
            noDependenyDiv.classList.remove(hide);
        }
    };

    private _resetDocumentUploadDialogWithPreselectedType = (selectedIndex: number = 0) => {
        const title = this._mdcTextFields['appEditModalAddDocument-title'];
        const upload = <HTMLInputElement>document.getElementById('appEditModalEditScreenshot_document_url');
        const select = this._mdcSelects['appEditModalAddDocument-type-select'];
        if (!(title && upload && select)) {
            return;
        }
        title.value = "";
        select.selectedIndex = selectedIndex;
        upload.value = "";
        upload.setAttribute("value", "");
    };

    private _validateDocumentDialog = () => {
        this._setSaveDocumentButtonDisabled(true);
        const select = this._mdcSelects['appEditModalAddDocument-type-select'];
        const title = this._mdcTextFields['appEditModalAddDocument-title'];
        const upload = <HTMLInputElement>document.getElementById('appEditModalEditScreenshot_document_url');
        if (!(title && upload && select)) {
            console.log("missing element title, upload or select");
            return;
        }
        if (select.selectedIndex >= 0 && this._validateMDCTextField(title, 1) && this._isValidInputValue(upload.value, 1)) {
            this._setSaveDocumentButtonDisabled(false);
        }
    };

    private _setSaveDocumentButtonDisabled = (disabled: boolean) => {
        const button = <HTMLButtonElement>document.getElementById('appEditModalAddDocument_save_button');
        if (!button) {
            console.log("Button 'appEditModalAddDocument_save_button' not found");
            return;
        }
        button.disabled = disabled;
    };

    private _setupAdministrateDependencyDialog = (): void => {
        const dialog = this._mdcDialogs['appEditModalAdministrateDependency'];
        if (!dialog) {
            console.error('setupAdministrateDependencyDialog: dialog not found');
            return;
        }
        dialog.listen('MDCDialog:closed', (e: dialog.MDCDialogCloseEventDetail) => {
            const dialogRoot = dialog.root || dialog.root_;
            const appName = dialogRoot.dataset.appname;
            const action = e.detail.action
            if (action == 'save') {
                const permission = dialogRoot.dataset.permission;
                const isSilentDep = dialogRoot.dataset.silentdependency === '1';
                this._setDependencyAccessPermission(appName, permission, isSilentDep)
            }
            if (action == 'delete') {
                this._handleRemoveDependency(appName);
            }
        });
        this._addAdministrateDependencyRadioEventListener();
    };

    private _addAdministrateDependencyRadioEventListener = (): void => {
        const dialog = this._mdcDialogs['appEditModalAdministrateDependency'];
        if (!dialog) {
            console.error('setupAdministrateDependencyDialog: dialog not found');
            return;
        }
        const dialogRoot = <HTMLElement>(dialog.root || dialog.root_);
        const radioInputs = Array.apply(null, dialogRoot.querySelectorAll('.permission-radio-input'));
        radioInputs.forEach((input: HTMLInputElement) => {
            input.addEventListener('change', () => {
                dialogRoot.dataset.permission = input.value;
            })
        });
    };

    private _openAdministrateDependencyDialog = (e:CustomEvent): void => {
        const index = e.detail.index;
        const mdcList: mdc.list.MDCList = this._mdcLists['app_dependency_list'];
        if (!mdcList) {
            console.error('_openAdministrateDependencyDialog: mdc list not found');
            return;
        }
        const target = <HTMLLIElement>mdcList.listElements[index];
        if (!target) {
            console.error('_openAdministrateDependencyDialog: list item not found');
            return;
        }

        if (target.classList.contains('mdc-list-item--disabled')) {
            return;
        }

        const dialog = this._mdcDialogs['appEditModalAdministrateDependency'];
        if (!dialog) {
            console.error('openAdministrateDependencyDialog: dialog not found');
            return;
        }

        const dialogRoot = dialog.root || dialog.root_;
        dialogRoot.dataset.appname = target.dataset.appname;
        dialogRoot.dataset.permission = target.dataset.permission;
        dialogRoot.dataset.silentdependency = target.dataset.silentdependency;
        this._renderAdministrateDependencyDialog();
        dialog.open();
    };

    private _renderAdministrateDependencyDialog = (): void => {
        const dialogRoot = <HTMLElement>document.getElementById('appEditModalAdministrateDependency');
        if (!dialogRoot) {
            console.error('renderAdministrateDependencyDialog: dialog not found');
            return;
        }
        const permission = (<any>Object).values(DependencyAccessPermissionValues).includes(dialogRoot.dataset.permission) ? <DependencyAccessPermissionValues>dialogRoot.dataset.permission : DependencyAccessPermissionValues.None;
        const isSilentDep = dialogRoot.dataset.silentdependency === '1';
        this._setDependencyAccessPermissionRadioButtons(permission);
        this._setDependencyIsSilentDependencyCheckbox(isSilentDep);
        this._setAdministrateDependencyDialogTitle(dialogRoot.dataset.appname);
    };

    private _setAdministrateDependencyDialogTitle = (app: string): void => {
        const header = <HTMLElement>document.getElementById('appEditModalAdministrateDependency-title');
        if (!header) {
            console.debug('setAdministrateDependencyDialogTitle: header element missing');
            return;
        }
        header.innerHTML = i18n.replaceTranslationPlaceholder(this._i18n.getTranslation('app_edit_dependencies_dep_administrate_dialog_title'), '{APPNAME}', app);
    };

    private _setDependencyAccessPermissionRadioButtons = (auth: DependencyAccessPermissionValues): void => {
        const none = this._mdcRadioButtons['appEditModalAdministrateDependency-option-none'];
        const read = this._mdcRadioButtons['appEditModalAdministrateDependency-option-read'];
        const readWrite = this._mdcRadioButtons['appEditModalAdministrateDependency-option-readwrite'];
        if (!(none && read && readWrite)) {
            console.error('setDependencyAccessPermissionRadioButtons: radio button not found');
            return;
        }
        none.checked = !!auth ? (auth == DependencyAccessPermissionValues.None) : none;
        read.checked = (auth == DependencyAccessPermissionValues.Read);
        readWrite.checked = (auth == DependencyAccessPermissionValues.ReadWrite);
    };

    private _setDependencyIsSilentDependencyCheckbox = (isSilentDependency:boolean) => {
        const checkbox = this._mdcCheckboxes['appEditModalAdministrateDependency-isSilentDependency'];
        checkbox.checked = !isSilentDependency;
    }

    private _patchAppWithData = (data: patchData, cbSuccess?: () => void, cbError?: (message: string) => void): void => {
        if (!data) {
            console.debug('patchAppWithData: data was empty');
        }
        const url = UrlHelpers.getLinkRelHref('self');
        const req = new Request(url, {
            method: 'PATCH',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/merge-patch+json'
            },
            body: JSON.stringify(data),
            credentials: "same-origin"
        });
        fetch(req)
            .then((r: Response) => {
                if (r.status !== 200) {
                    r.text().then((text) => {
                        throw `request failed with error code ${r.status} and message '${text}'`;
                    })
                }
                if (cbSuccess) {
                    cbSuccess();
                }
                return
            })
            .catch((e: string) => {
                console.log(`Request to ${url} failed! Error: '${e}'`);
                if (cbError) {
                    cbError(e);
                }
            })
    };

    private _removeDependency = (app: string): void => {
        if (!app) {
            console.debug('removeDependencyAccessPermission: app is empty');
            return;
        }
        const mainAppName = UrlHelpers.getMetaData('appName');
        const data = {
            appName: mainAppName,
            dependencyList: {}
        } as patchData
        data.dependencyList[app] = null;

        this._patchAppWithData(data, () => {
            this._removeDependencyListItemForApp(app);
        }, (message) => {
            console.error(`remove dependency failed: ${message}`);
            this._showErrorNotification('appEdit-notification', message);
        });
    };

    private _setDependencyAccessPermission = (app: string, permission: string, isSilentDependency: boolean): void => {
        if (!app) {
            console.debug('removeDependencyAccessPermission: app is empty');
            return;
        }
        const mainAppName = UrlHelpers.getMetaData('appName');
        const data = {
            appName: mainAppName,
            dependencyList: {}
        } as patchData
        permission = DependencyAccessPermissionValues.parse(permission).toString();
        data.dependencyList[app] = {appName: app, accessPermission: permission, isSilentDependency: isSilentDependency}
        this._patchAppWithData(data, () => {
            this._addDependencyToULElement(app, permission, isSilentDependency);
        }, (message) => {
            console.error(`set dependency failed: ${message}`);
            this._showErrorNotification('appEdit-notification', message)
        });
    };

    private _removeDependencyListItemForApp = (app: string): void => {
        console.log(`remove '${app}'`);
        this._removeDependencyFromULElement(app);
    };

    private _addNewDependency = (app: string, cbSuccess?: () => void): void => {
        if (!app) {
            console.debug('removeDependencyAccessPermission: app is empty');
            return;
        }
        const mainAppName = UrlHelpers.getMetaData('appName');
        const data = {
            appName: mainAppName,
            dependencyList: {}
        } as patchData
        data.dependencyList[app] = {appName: app, accessPermission: "none", isSilentDependency: true};

        this._patchAppWithData(data, cbSuccess, (message) => {
            console.error(`add dependency failed: ${message}`);
            this._showErrorNotification('appEdit-notification', message)
        });
    };

    private _writeAppListToMeta = (dto: appDependencyList): void => {
        const data = {} as appList
        dto.apps.forEach(item => {
            data[item.appName] = {appName: item.appName, displayName: item.displayName} as appListDetail
        })
        const appListKey = 'appList';
        UrlHelpers.setMetaData(appListKey, JSON.stringify(data));
    };

    private _readAppListFromMeta = (): appList => {
        const appListKey = 'appList';
        const data = UrlHelpers.getMetaData(appListKey);
        if (!data) {
            console.debug('readAppListFromMeta: no app list data found');
            return undefined;
        }
        return (JSON.parse(data) as appList);
    };

    private _writeDependencyListToULElement = (d: { [id: string]: dependencyDetail }): void => {
        const elementId = 'app_dependency_list';
        const ul = <HTMLElement>document.getElementById(elementId);
        if (!ul) {
            console.debug(`readDependencyListFromULElement: element with id ${elementId} not found`);
            return;
        }
        ul.dataset.dependencylist = JSON.stringify(d);
    };

    private _readDependencyListFromULElement = (): { [id: string]: dependencyDetail } => {
        const elementId = 'app_dependency_list';
        const ul = <HTMLElement>document.getElementById(elementId);
        if (!ul) {
            console.debug(`readDependencyListFromULElement: element with id ${elementId} not found`);
            return undefined;
        }
        return JSON.parse(ul.dataset.dependencylist);
    };

    private _addDependencyToULElement = (appName: string, accessPermission: string, isSilentDep:boolean): void => {
        if (!appName || !accessPermission) {
            console.debug('addDependencyToULElement: parameter empty');
            return;
        }
        const deps = this._readDependencyListFromULElement();
        if (!deps) {
            console.error('addDependencyToULElement: cannot read data');
            return;
        }
        deps[appName] = {appName: appName, accessPermission: accessPermission, isSilentDependency: isSilentDep} as dependencyDetail
        this._writeDependencyListToULElement(deps);
    };

    private _removeDependencyFromULElement = (appName: string): void => {
        if (!appName) {
            console.debug('removeDependencyFromULElement: appName missing');
            return;
        }
        const deps = this._readDependencyListFromULElement();
        if (!deps) {
            console.error('removeDependencyFromULElement: cannot read data');
            return;
        }
        delete deps[appName];
        this._writeDependencyListToULElement(deps);
    };

    private _setDependenciesListObserver = (): void => {
        const elementId = 'app_dependency_list';
        const ul = <HTMLElement>document.getElementById(elementId);
        if (!ul) {
            console.debug(`setDependenciesListObserver: element with id ${elementId} not found`);
            return undefined;
        }
        const observedAttributeName = 'data-dependencylist';
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type == "attributes" && mutation.attributeName == observedAttributeName) {
                    this._renderDependenciesList();
                }
            });
        });
        observer.observe(ul, {
            attributeFilter: [observedAttributeName],
        });
    };

    private _renderDependenciesList = (): void => {
        const listId = 'app_dependency_list';
        console.debug('### RENDERING LIST ###');
        console.debug('get list element');
        const ul = <HTMLElement>document.getElementById(listId);
        if (!ul) {
            console.debug(`renderDependenciesList: element with id ${listId} not found`);
            return;
        }
        console.debug('get data');
        const data = this._readDependencyListFromULElement();
        if (!data) {
            console.debug('renderDependenciesList: data not found');
            return;
        }
        console.debug('data:', data)
        console.debug('get app list');
        const appList = this._readAppListFromMeta();
        if (!appList) {
            console.debug('renderDependenciesList: appList not found');
        }
        console.debug('loop dependencies');
        const disable = false; //ul.dataset.disableitems == "1"; //todo: static false until  01/01/2022
        const listItems: HTMLLIElement[] = [];
        for (let appsKey in data) {
            console.debug(`loop iteration for ${appsKey}`);
            const app = data[appsKey];
            const displayName = appList[app.appName] ? appList[app.appName].displayName : app.appName;
            const addNewTag = this._isNewAddedApp(app.appName);
            const li = this._getDependenciesListItem(app.appName, displayName, app.accessPermission, app.isSilentDependency, addNewTag, disable);
            listItems.push(li);
        }
        console.debug('new list elements:', listItems);
        listItems.sort(function (a, b) {
            const appNameA = a.dataset.appname.toLowerCase();
            const appNameB = b.dataset.appname.toLowerCase();
            if (appNameA < appNameB) {
                return -1;
            }
            if (appNameA > appNameB) {
                return 1;
            }
            return 0;
        })
        ul.innerHTML = '';
        listItems.forEach((item) => {
            ul.appendChild(item)
        });
        this.resetDependenciesList();
        this._showOrHideNoDependencyMessage();
    };

    private _getDependenciesListItem = (appName: string, displayName: string, accessPermission: string, isSilentDep: boolean, newItem: boolean, disabled: boolean): HTMLLIElement => {
        const li = <HTMLLIElement>document.createElement('li');
        li.id = `dep-${appName}`;
        const itemClassList: string[] = ['mdc-list-item', 'app-dependency'];
        if (disabled) {
            itemClassList.push('mdc-list-item--disabled');
        }
        li.classList.add(...itemClassList);
        li.setAttribute('data-appname', appName);
        li.setAttribute('data-permission', accessPermission);
        li.setAttribute('data-silentdependency', isSilentDep ? '1' : '0');
        li.title = this._i18n.getTranslation('app_edit_dependencies_dep_item_btn_authorize_title');

        if (newItem) {
            const newItem = `<span class="mdc-list-item__graphic material-icons" style="margin-right: 5px !important;">fiber_new</span>`;
            li.insertAdjacentHTML('beforeend', newItem);
        }
        const dependencyPermissionText = i18n.replaceTranslationPlaceholder(this._i18n.getTranslation('app_edit_card_dependencies_listItem_secondaryText_Access'), '{PERMISSION}', this._mapPermissionValueToI18n(accessPermission));
        const dependencyAutoSubscribeText = i18n.replaceTranslationPlaceholder(this._i18n.getTranslation('app_edit_card_dependencies_automatic_subscription_label'),'{STATE}',this.getIsSilentDependencyStateTranslation(isSilentDep));
        const listContent = `
            <div class="mdc-list-item__text">
                <span class="mdc-list-item__primary-text">${displayName} <span class="appEdit-dependencies_dependency-list_appId-label">(${appName})</span></span>
                <span class="mdc-list-item__secondary-text">${dependencyPermissionText}&nbsp;|&nbsp;${dependencyAutoSubscribeText}</span>
            </div>`
        li.insertAdjacentHTML('beforeend', listContent);
        if (!disabled) {
            li.insertAdjacentHTML('beforeend', '<i class="material-icons" style="margin-right: 12px">edit</i>');
        }
        return li;
    };

    private getIsSilentDependencyStateTranslation = (isSilentDependency:boolean):string => {
        let key = 'app_edit_card_dependencies_automatic_subscription_on'
        if (isSilentDependency) {
            key = 'app_edit_card_dependencies_automatic_subscription_off';
        }
        return this._i18n.getTranslation(key);
    }

    private resetDependenciesList = () => {
        const listId = 'app_dependency_list';
        const mdcList: mdc.list.MDCList = this._mdcLists[listId];
        if (!mdcList) {
            console.log(`resetDependenciesList: cannot find mdcList with id ${listId}`);
            return
        }

        mdcList.destroy();

        const newMdcList: mdc.list.MDCList = mdc.list.MDCList.attachTo(document.getElementById(listId));
        mdcList.listen('MDCList:action', this._openAdministrateDependencyDialog);

        this._mdcLists[listId] = newMdcList;
    }

    private _mapPermissionValueToI18n = (permission: string): string => {
        permission = DependencyAccessPermissionValues.parse(permission).toString();
        const kv = {
            "none": this._i18n.getTranslation('app_edit_permisson_short_none'),
            "read": this._i18n.getTranslation('app_edit_permisson_short_read'),
            "readwrite": this._i18n.getTranslation('app_edit_permisson_short_readwrite')
        }
        return kv[permission];
    };

    private _appendNewAddedApp = (appName: string): void => {
        if (!appName) {
            return
        }
        this._newAddedApps = this._newAddedApps || [];
        this._newAddedApps.push(appName);
    };

    private _isNewAddedApp = (appName: string): boolean => {
        return !!appName && !!this._newAddedApps ? this._newAddedApps.indexOf(appName) >= 0 : false;
    };
}

enum PatchAppAction {
    VIEWPROP = 'viewProp',
    TECHNICAL = 'technical',
    MEDIA = 'media',
    GENERAL = 'general',
    REMOVEVIEWPROP = 'removeViewProp',
    REMOVEMEDIA = 'removeMedia',
    REMOVEDOCUMENT = 'remdoc',
    ADDDOCUMENT = 'adddoc',
    BILLING = 'billingprop'
}

export interface patchAppRequest<T> {
    appName: string
    action: PatchAppAction
    data: T
}

export interface appRequestGeneral {
    icon: string
}

export interface appRequestViewProp {
    displayName: string
    description: string
    summary: string
    language: string
}

export interface appPricingProp {
    billingMarkup: string
    language: string
    useMetricDataForDescription: boolean
    priceListAnnotation: string
}

export interface appRequestTechnicalDetails {
    endpoint: string
    dependencies: string[]
    isBackground: boolean
    subscriptionNotificationMail: string
}

export interface appRequestDocument {
    name: string
    type: string
    link: string
    uploadTime: string
    language: string
}

export interface collapsible {
    targets: string[],
    buttons: string[]
}

export interface mdcInputValidation {
    input: textField.MDCTextField
    minLength: number
}

interface inputButtonCallback {
    (url: string): void;
}

enum DataAttributes {
    KEY = 'data-key',
    VALUE = 'data-documentlink',
    NAME = 'data-name'
}

export interface appDependencyList {
    apps: appDependencyItem[]
}

export interface appDependencyItem {
    appName: string
    displayName: string
    providerName: string
    isApproved: boolean
}

export interface appList {
    apps: { [id: string]: appListDetail }
}

export interface appListDetail {
    appName: string
    displayName: string
}

export interface dependencyDetail {
    appName: string
    accessPermission: string
    isSilentDependency: boolean
}

export interface patchData {
    appName: string
    dependencyList: { [id: string]: patchDataAppDependency }
}

export interface patchDataAppDependency {
    appName: string
    accessPermission: string
    isSilentDependency: boolean
}

enum DependencyAccessPermissionValues {
    None = "none",
    Read = "read",
    ReadWrite = "readwrite"
}

namespace DependencyAccessPermissionValues {
    export function includes(value: string): boolean {
        return (<any>Object).values(DependencyAccessPermissionValues).includes(value);
    }
    export function parse(value: string): DependencyAccessPermissionValues {
        value = value.toLowerCase();
        return DependencyAccessPermissionValues.includes(value) ? <DependencyAccessPermissionValues>value : DependencyAccessPermissionValues.None;
    }
}